// src/pages/bloomterminal/LookerStudio.jsx (formerly dashboard)
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useOutletContext } from 'react-router-dom';
import { Maximize, Minimize, RefreshCw } from 'lucide-react';
import MobilePageTopBar, { MOBILE_TOPBAR_CONTROL_H } from '../components/MobilePageTopBar';

const LookerStudio = () => {
  const { t } = useTranslation();
  // Mobile hamburger handler supplied by DashboardLayout via <Outlet context>
  // (same mechanism Analytics/MapPage use). Optional-chained everywhere it's
  // used so this page never crashes if the context isn't wired up.
  const { onMenuClick } = useOutletContext() || {};
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  // Handle toggling fullscreen
  const toggleFullscreen = () => {
    setIsFullscreen(!isFullscreen);
  };
  
  // Handle refreshing the iframe
  const handleRefresh = () => {
    setIsLoading(true);
    // Force iframe refresh by recreating it
    const iframe = document.getElementById('looker-iframe');
    if (iframe) {
      const parent = iframe.parentNode;
      const newIframe = iframe.cloneNode(true);
      parent.replaceChild(newIframe, iframe);
      
      // Reset loading when iframe loads
      newIframe.onload = () => setIsLoading(false);
    }
  };
  
  // Handle iframe load
  const handleIframeLoad = () => {
    setIsLoading(false);
  };
  
  // Add fullscreen event listener for ESC key handling
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && isFullscreen) {
        setIsFullscreen(false);
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isFullscreen]);

  return (
    <>
      {/* Only show header when not in fullscreen */}
      {!isFullscreen && (
        <>
          {/* Desktop (lg+): original layout, pixel-identical — Refresh and
              Fullscreen grouped together, left-aligned. */}
          <div className="hidden lg:flex justify-between items-center mb-4">
            <div className="flex items-center space-x-4">
              <button
                onClick={handleRefresh}
                className="flex items-center px-3 py-1 bg-gray-800 rounded text-gray-300 hover:bg-gray-700"
              >
                <RefreshCw size={16} className="mr-2" />
                Refresh
              </button>
              <button
                onClick={toggleFullscreen}
                className="flex items-center px-3 py-1 bg-gray-800 rounded text-gray-300 hover:bg-gray-700"
              >
                <Maximize size={16} className="mr-2" />
                Fullscreen
              </button>
            </div>
          </div>

          <MobilePageTopBar onMenuClick={onMenuClick} isFullscreen={isFullscreen} onEnterFullscreen={toggleFullscreen} onExitFullscreen={toggleFullscreen}>
            <button onClick={handleRefresh} className={`flex ${MOBILE_TOPBAR_CONTROL_H} items-center px-3 bg-gray-800 rounded text-gray-300 hover:bg-gray-700`}>
              <RefreshCw size={16} className="mr-2" />Refresh
            </button>
          </MobilePageTopBar>
        </>
      )}
      
      {/* Looker Studio iframe container */}
      <div 
        className={`relative bg-gradient-to-br from-gray-900 to-green-900/10 rounded-lg border border-green-900/30 shadow-lg overflow-hidden
                    ${isFullscreen ? 'fixed inset-0 z-50' : 'h-[calc(100vh-140px)] -mx-3'}`}
      >
        {/* Loading overlay */}
        {isLoading && (
          <div
            className="skeleton-sheen !absolute inset-0 z-10"
            role="status"
            aria-busy="true"
            aria-label="Loading"
          />
        )}
        
        {/* Looker Studio iframe */}
        <iframe
          id="looker-iframe"
          src="https://lookerstudio.google.com/embed/reporting/cd6e8842-17cf-4ef2-8540-10c40a02df1d/page/tEnnC"
          className="w-full h-full bg-white"
          frameBorder="0"
          style={{ minHeight: isFullscreen ? '100vh' : '700px' }}
          allowFullScreen
          onLoad={handleIframeLoad}
        ></iframe>
      </div>
      
      {/* Fullscreen exit button overlay */}
      {isFullscreen && (
        <div className="fixed top-4 right-4 z-50 flex space-x-2">
          <button 
            onClick={handleRefresh}
            className="bg-black/80 p-3 rounded-full cursor-pointer hover:bg-black"
          >
            <RefreshCw size={20} className="text-white" />
          </button>
          <button 
            onClick={toggleFullscreen}
            className="bg-black/80 p-3 rounded-full cursor-pointer hover:bg-black"
          >
            <Minimize size={20} className="text-white" />
          </button>
        </div>
      )}
    </>
  );
};

export default LookerStudio;